import type { i18n } from 'i18next';

import { newKey } from '@ricoh/ria.client.core/utils/data/key';
import { isType } from '@ricoh/ria.client.core/utils/data/object';

import type { BatchType } from '~/common/model/api/batch-type';
import type { ProcessFlow, ProcessFlowStepTaskRoutingCloud, RoutingFileCsv } from '~/common/model/api/batch-type/process-flow';

import processFlowLogic from '~/common/logic/feature/settings/batch-type-library/process-flow';

/**
 * Duplicate BatchType.
 *
 * @param {i18n['t']} t Translation function.
 * @param {BatchType} original Original BatchType.
 *
 * @returns {BatchType} Duplicate BatchType.
 */
function duplicateBatchType(t: i18n['t'], original: BatchType): BatchType {
  return {
    ...original,
    name: t(`feature:settings.batchTypeLibrary.grid.duplicateName`, {
      name: original.name,
    }),
    fields: original.fields.map((field) => ({
      ...field,
      id: newKey(),
    })),
    documents: original.documents.map((document) => ({
      ...document,
      id: newKey(),
      rejectReasons: document.rejectReasons.map((rejectReason) => ({
        ...rejectReason,
        id: newKey(),
      })),
      fields: document.fields.map((field) => ({
        ...field,
        id: newKey(),
      })),
      variables: document.variables.map((variable) => ({
        ...variable,
        id: newKey(),
      })),
      businessRules: document.businessRules.map((businessRule) => ({
        ...businessRule,
        id: newKey(),
      })),
      stamps: document.stamps.map((stamp) => ({
        ...stamp,
        id: newKey(),
      })),
    })),
    variables: original.variables.map((variable) => ({
      ...variable,
      id: newKey(),
    })),
    businessRules: original.businessRules.map((businessRule) => ({
      ...businessRule,
      id: newKey(),
    })),
  };
}

/**
 * Duplicate ProcessFlow.
 *
 * @param {BatchType} original Original ProcessFlow.
 *
 * @returns {BatchType} Duplicate ProcessFlow.
 */
function duplicateProcessFlow(original: ProcessFlow | undefined): ProcessFlow | undefined {
  if (!original) {
    return undefined;
  }

  return {
    ...original,
    steps: original.steps.map((step) => {
      if (processFlowLogic.step.isStep.task<ProcessFlowStepTaskRoutingCloud>(step, 'routing')) {
        return {
          ...step,
          id: newKey(),
          settings: {
            ...step.settings,
            files: step.settings.files.map((file) => {
              if (isType<RoutingFileCsv>(file, 'csv')) {
                return {
                  ...file,
                  id: newKey(),
                  template: {
                    ...file.template,
                    id: newKey(),
                    fields: file.template.fields.map((field) => ({
                      ...field,
                      id: newKey(),
                    })),
                  },
                };
              }

              return {
                ...file,
                id: newKey(),
              };
            }),
          },
        };
      }

      return {
        ...step,
        id: newKey(),
      };
    }),
  };
}

/**
 * Create Duplicate BatchType.
 *
 * @param {i18n['t']} t Translation function.
 * @param {[BatchType, ProcessFlow | undefined]} originalData Original data.
 *
 * @returns {[BatchType, ProcessFlow | undefined]} Duplicate data.
 */
export function createDuplicateBatchType(
  t: i18n['t'],
  [originalBatchType, originalProcessFlow]: [BatchType, ProcessFlow | undefined],
): [batchType: BatchType, processFlow: ProcessFlow | undefined] {
  const batchType = duplicateBatchType(t, originalBatchType);
  const processFlow = duplicateProcessFlow(originalProcessFlow);

  return [batchType, processFlow];
}
